import logger from '~/services/logger';

/**
 * Get JSON data from a given URL with optional caching in sessionStorage.
 * @param url - The URL to fetch JSON data from
 * @param method - HTTP method (default: 'GET')
 * @param options - Additional fetch options
 * @param body - Request body for POST/PUT requests
 * @param headers - Request headers
 * @param timeout - Timeout in milliseconds (default: 60000 ms)
 * @param sessionStorageKey - Key to use for sessionStorage caching
 * @param sessionStorageRevalidate - Time in milliseconds to revalidate the cached data
 * @returns Promise<JSON data> or undefined on error
 */
export async function requestJsonData({
  url,
  method = 'GET',
  options = { cache: 'no-cache' },
  body = null,
  headers = {
    'Content-Type': 'application/json',
  },
  timeout = 60_000,
  sessionStorageKey,
  sessionStorageRevalidate,
}: {
  url: string;
  method?: string;
  options?: any;
  body?: any;
  headers?: any;
  timeout?: number;
  sessionStorageKey?: string;
  sessionStorageRevalidate?: number;
}): Promise<any> {
  const sessionStorageValue = sessionStorageCache({
    sessionStorageKey,
    sessionStorageRevalidate,
  });

  if (sessionStorageValue) return sessionStorageValue;

  const controller: AbortController = new AbortController();

  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const {
      signal,
    }: {
      signal: AbortSignal;
    } = controller;

    const response = await fetch(url, {
      ...options,
      method,
      body,
      headers,
      signal,
    });

    if (!response.ok) throw new Error('Network response was not ok');

    const jsonData: any = await response.json();

    if (!jsonData) throw new Error('No JSON data found');

    if (sessionStorageKey)
      sessionStorageCache({
        sessionStorageKey,
        value: jsonData,
        sessionStorageRevalidate,
      });

    return jsonData;
  } catch (error: any) {
    logger.error(`Error in requestJsonData: ${error.message}`);
  } finally {
    clearTimeout(timeoutId);
  }
}

function sessionStorageCache({
  sessionStorageKey,
  value,
  sessionStorageRevalidate,
}: {
  sessionStorageKey?: string;
  value?: any;
  sessionStorageRevalidate?: number;
}): any {
  try {
    if (!sessionStorageKey) return undefined;

    const sessionStorageRevalidateKey: string = `${sessionStorageKey}-revalidate`;

    const sessionStorageRevalidateRaw: string | null =
      window.sessionStorage.getItem(sessionStorageRevalidateKey);

    const sessionStorageRevalidateNumber: number | null = Number(
      sessionStorageRevalidateRaw
    );

    if (
      sessionStorageRevalidateRaw &&
      !isNaN(sessionStorageRevalidateNumber) &&
      sessionStorageRevalidate
    ) {
      const isStale: boolean =
        Date.now() - sessionStorageRevalidateNumber > sessionStorageRevalidate;

      if (isStale) {
        window.sessionStorage.removeItem(sessionStorageKey);
        window.sessionStorage.removeItem(sessionStorageRevalidateKey);
        return undefined;
      }
    }

    const sessionStorageValue: string | null =
      window.sessionStorage.getItem(sessionStorageKey);

    if (sessionStorageValue) return JSON.parse(sessionStorageValue);

    if (!value) return undefined;

    window.sessionStorage.setItem(sessionStorageKey, JSON.stringify(value));

    window.sessionStorage.setItem(
      sessionStorageRevalidateKey,
      JSON.stringify(Date.now())
    );

    return value;
  } catch (error: any) {
    logger.error(`Error in sessionStorageCache: ${error.message}`);
    return undefined;
  }
}
